import React, { useState, useMemo } from 'react';
import { Clock, CheckCircle, AlertCircle, TrendingUp, Calendar, ChevronRight, ChevronDown, ChevronUp } from 'lucide-react';
import { useHoursOfRestData } from '../../../../hooks/domain/useHoursOfRestData';
import { hoursOfRestRules } from '../../../../logic/regulations/hoursOfRest';

/**
 * HoursOfRestSummaryCard - Displays crew member's hours of rest compliance
 * Shows last 30 days compliance summary with breach indicators
 */
const HoursOfRestSummaryCard = ({ crewId, onViewDetails }) => {
  // Collapsed state for progressive disclosure
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: horRecords } = useHoursOfRestData();
  const rules = useMemo(() => hoursOfRestRules(), []);

  // Calculate compliance for this crew member
  const compliance = useMemo(() => {
    if (!crewId || !horRecords) {
      return { hasData: false, message: 'No data' };
    }
    return rules.checkCrewCompliance(horRecords, crewId, { days: 30 });
  }, [horRecords, crewId, rules]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short'
    });
  };

  // Get status color and icon
  const getStatusDisplay = () => {
    if (!compliance.hasData) {
      return {
        color: 'bg-gray-50 text-gray-600',
        icon: Clock,
        text: 'No Data'
      };
    }
    if (compliance.compliant) {
      return {
        color: 'bg-green-50 text-green-700',
        icon: CheckCircle,
        text: 'Compliant'
      };
    }
    return {
      color: 'bg-red-50 text-red-700',
      icon: AlertCircle,
      text: `${compliance.breaches.total} Breach${compliance.breaches.total !== 1 ? 'es' : ''}`
    };
  };

  const status = getStatusDisplay();

  return (
    <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
      {/* Header - Inside Card */}
      <div className="bg-slate-700 px-5 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-white" />
            <h3 className="text-base font-semibold text-white">Hours of Rest</h3>
          </div>
          <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ${status.color}`}>
            <status.icon className="h-3 w-3 flex-shrink-0" />
            <span>{status.text}</span>
          </span>
        </div>
      </div>

      <div className="px-6 py-5">
        {compliance.hasData ? (
          <div className="space-y-4">
            {/* Stats Grid - Always visible */}
            <div className="grid grid-cols-2 gap-3 pb-3 border-b border-gray-100">
              <div className="text-center p-2 bg-gray-50 rounded-md">
                <p className="text-lg font-semibold text-gray-900">{compliance.averages.dailyRest}h</p>
                <p className="text-xs text-gray-500">Avg Daily Rest</p>
              </div>
              <div className="text-center p-2 bg-gray-50 rounded-md">
                <p className="text-lg font-semibold text-gray-900">{compliance.recordCount}</p>
                <p className="text-xs text-gray-500">Days Recorded</p>
              </div>
            </div>

            {/* Expandable Content */}
            {isExpanded && (
              <>
                {/* Date Range */}
                {compliance.dateRange && (
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>
                      {formatDate(compliance.dateRange.from)} - {formatDate(compliance.dateRange.to)}
                    </span>
                  </div>
                )}

                {/* Breach Summary */}
                {compliance.breaches.total > 0 && (
                  <div className="bg-red-50 rounded-md p-3 space-y-2">
                    <p className="text-xs font-medium text-red-700">Compliance Issues (Last 30 Days):</p>
                    <div className="space-y-1 text-xs text-red-600">
                      {compliance.breaches.daily > 0 && (
                        <p>• {compliance.breaches.daily} daily rest/work breach{compliance.breaches.daily !== 1 ? 'es' : ''}</p>
                      )}
                      {compliance.breaches.weekly > 0 && (
                        <p>• {compliance.breaches.weekly} weekly limit breach{compliance.breaches.weekly !== 1 ? 'es' : ''}</p>
                      )}
                      {compliance.breaches.restSplit > 0 && (
                        <p>• {compliance.breaches.restSplit} rest split violation{compliance.breaches.restSplit !== 1 ? 's' : ''}</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Compliant indicator */}
                {compliance.compliant && (
                  <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 px-2 py-1.5 rounded-md">
                    <CheckCircle className="h-3.5 w-3.5" />
                    <span>All STCW rest requirements met</span>
                  </div>
                )}

                {/* Latest record info */}
                {compliance.latestRecord && (
                  <div className="text-xs text-gray-500 border-t border-gray-100 pt-2 mt-2">
                    <span className="font-medium">Latest entry:</span>{' '}
                    {formatDate(compliance.latestRecord.date)} - {compliance.latestRecord.restHours}h rest
                  </div>
                )}

                {/* View Details Button */}
                {onViewDetails && (
                  <button
                    onClick={onViewDetails}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-purple-600 bg-white border border-purple-600 rounded-md hover:bg-purple-50 transition-colors"
                  >
                    View Hours of Rest Log
                    <ChevronRight className="h-4 w-4" />
                  </button>
                )}
              </>
            )}

            {/* Expand/Collapse Toggle */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="w-full flex items-center justify-center gap-1 py-2 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  Show less
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  Show more
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="text-center py-4">
            <Clock className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500 italic text-sm">No hours of rest records</p>
            <p className="text-xs text-gray-400 mt-1">Records will appear here once logged</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HoursOfRestSummaryCard;
